import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AuthActions } from "../../../../store/ducks/auth";
import {
	CopilotPatientReference,
	fetchOperationalPatients,
	isAbortError,
} from "../../services/clinicalDashboardService";

const PAGE_SIZE = 8;

export interface PatientSelection {
	id: number;
	name: string;
	initials: string;
	photoUrl: string | null;
	original: CopilotPatientReference;
}

const mapPatient = (patient: CopilotPatientReference): PatientSelection => {
	const name = patient.nome || "";
	const names = name.trim().split(/\s+/).filter(Boolean);
	return {
		id: patient.id,
		name,
		initials:
			names.length > 1
				? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
				: name.charAt(0).toUpperCase(),
		photoUrl: patient.foto || null,
		original: patient,
	};
};

export const usePatientSelectionForCopilotModalViewModel = (
	isOpen: boolean,
	onClose: () => void,
	onSelectPatient: (patient: CopilotPatientReference) => void
) => {
	const dispatch = useDispatch();
	const token = useSelector((state: any) => state?.auth?.user?.token) || "";
	const [searchQuery, setSearchQuery] = useState("");
	const [patients, setPatients] = useState<PatientSelection[]>([]);
	const [page, setPage] = useState(1);
	const [hasExtraPatients, setHasExtraPatients] = useState(false);
	const [patientsLoading, setPatientsLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const requestRef = useRef<AbortController | null>(null);
	const requestSequence = useRef(0);

	const load = useCallback(
		async (nextPage: number, append: boolean, query: string) => {
			if (!token) {
				setError("Sua sessão não está disponível. Entre novamente.");
				return;
			}

			requestRef.current?.abort();
			const controller = new AbortController();
			requestRef.current = controller;
			const sequence = ++requestSequence.current;
			setError(null);
			setPatientsLoading(true);
			setLoadingMore(append);

			try {
				const result = await fetchOperationalPatients({
					token,
					search: query,
					page: nextPage,
					limit: PAGE_SIZE,
					signal: controller.signal,
				});
				if (sequence !== requestSequence.current) return;
				const incoming = result.items.map(mapPatient);
				setPatients((current) => {
					if (!append) return incoming;
					return Array.from(
						new Map(
							[...current, ...incoming].map((item) => [item.id, item])
						).values()
					);
				});
				setPage(result.paginacao?.pagina || nextPage);
				setHasExtraPatients(!!result.paginacao?.temProxima);
			} catch (requestError) {
				if (
					!isAbortError(requestError) &&
					sequence === requestSequence.current
				) {
					if ((requestError as any)?.status === 401) {
						dispatch(AuthActions.auth_logout());
					}
					setError(
						(requestError as Error)?.message ||
							"Não foi possível carregar os pacientes."
					);
				}
			} finally {
				if (sequence === requestSequence.current) {
					setPatientsLoading(false);
					setLoadingMore(false);
				}
			}
		},
		[token, dispatch]
	);

	useEffect(() => {
		if (!isOpen) {
			requestSequence.current += 1;
			requestRef.current?.abort();
			return;
		}
		setSearchQuery("");
		setPatients([]);
		setPage(1);
		setHasExtraPatients(false);
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		const timeout = setTimeout(() => load(1, false, searchQuery), 300);
		return () => {
			clearTimeout(timeout);
			requestSequence.current += 1;
			requestRef.current?.abort();
		};
	}, [searchQuery, isOpen, load]);

	const handleSelect = (patient: PatientSelection) => {
		onSelectPatient(patient.original);
		onClose();
	};

	return {
		searchQuery,
		filteredPatients: patients,
		hasExtraPatients,
		patientsLoading,
		loadingMore,
		error,
		handleSearchChange: setSearchQuery,
		handleSelect,
		handleLoadMore: () => load(page + 1, true, searchQuery),
		retry: () => load(1, false, searchQuery),
	};
};
